/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled/macro'
import colors from '@sportninja/common/constants/appColors'
import dayjs from 'dayjs'
import { useCallback, useLayoutEffect, useState } from 'react'

import DatePicker from '../../../components/DatePicker'
import req from '@sportninja/common/api/request'
// import FormInput from '../../../components/Form/FormInput'
import Icon from '../../../components/Icon'
import { Flex } from '../../../components/Layout'
import CurrencyInput from '../../../components/CurrencyInput'
import BaseActionButton from '../../../components/Page/BaseActionButton'
import SuspensionInput from '../../../components/Suspensions/SuspensionInput'
import SuspensionSelect from '../../../components/Suspensions/SuspensionSelect'
import SuspensionSubmitButton from '../../../components/Suspensions/SuspensionSubmitButton'
import SearchContainer from '../../Search/SearchContainer'
import ToggleSection from './ToggleSection'
import { observer } from 'mobx-react-lite'
import { useMobxStore } from '../../../state'
import { useHistory } from 'react-router-dom'
import LoadingSpinner from '../../../components/LoadingSpinner'

export const RegistrationSettingsTitle = styled.div`
  font-weight: 500;
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 16px;
`

const RegistrationSettings = observer(({ onClose, onConfirm }) => {
  const {
    registrationTypes: {
      state: { currency_type, registration_payment_issue_type },
    },
    registration: {
      updateRegistrationSettings,
      state: { registrationSettings, isLoading, registration },
    },
  } = useMobxStore()

  const [regBy, setRegBy] = useState(1)
  const [hasCopiedLink, setHasCopiedLink] = useState(false)
  const [backgroundImage, setBackgroundImage] = useState(null)

  const updateInfoWithType = (info, type) => {
    updateRegistrationSettings({
      [type]: info,
    })
  }

  const updateBackgroundImage = useCallback(async () => {
    try {
      let reader = new FileReader()

      reader.onloadend = async () => {
        const imageData = reader.result
        if (!imageData) {
          return
        }

        await req(
          `/schedules/${registration?.schedule_id}/registration/image`,
          {
            method: 'POST',
            body: imageData,
          }
        )
      }

      reader.readAsDataURL(backgroundImage)
    } catch (err) {
      console.error('There was an error uploading the image', err)
    }
  }, [backgroundImage, registration?.schedule_id])

  if (isLoading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          margin-top: 100px;
        `}
      >
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <section
      id='registration'
      css={css`
        width: 100%;
        height: 100%;
        overflow-y: auto;
      `}
    >
      <div
        id='registration-body'
        css={css`
          display: flex;
          border-bottom: 1px solid ${colors.SOFT_STEEL};
          max-width: 70%;
          margin: 0 auto;
          padding: 80px 0 32px 0;

          @media (max-width: 768px) {
            flex-direction: column;
          }
        `}
      >
        <div
          id='registration-body-left'
          css={css`
            flex: 1;
            padding-right: 20px;
          `}
        >
          <div
            css={css`
              margin-bottom: 20px;
            `}
          >
            <ToggleSection
              title='Registration Option'
              description='When turned ON, users can use the registration flow to create their teams for this competition.'
              isEnabled={registrationSettings?.online_registration_open}
              onClick={() =>
                updateRegistrationSettings({
                  online_registration_open:
                    !registrationSettings?.online_registration_open,
                })
              }
            />
          </div>
          <RegistrationSettingsTitle>General Info</RegistrationSettingsTitle>
          <div
            css={css`
              display: flex;
              gap: 20px;

              @media (max-width: 768px) {
                flex-direction: column;
                gap: 0;
              }
            `}
          >
            <SuspensionSelect
              enabledAllItemsOption={false}
              showSeparator={false}
              required
              useFullWidth
              label='Registration by'
              placeholder='-'
              value={regBy}
              onChange={(e) => setRegBy(e)}
              options={[
                { text: 'Per Team', value: 1 },
                // { text: 'Per Division', value: 2 },
              ]}
            />
            <SuspensionInput
              wrapCss={css`
                min-width: 178px;
              `}
              height={40}
              label='Max Number of Team Slots'
              id='team_slots'
              type='number'
              min={0}
              step={1}
              value={registrationSettings?.max_teams}
              onChange={({ target }) =>
                updateRegistrationSettings({ max_teams: target.value })
              }
            />
          </div>
          <div
            css={css`
              display: flex;
              gap: 20px;

              @media (max-width: 768px) {
                flex-direction: column;
                gap: 0;
                margin: 8px 0 12px 0;
              }
            `}
          >
            <SuspensionSelect
              showSeparator={false}
              enabledAllItemsOption={false}
              useFullWidth
              required
              label='Currency'
              placeholder='-'
              value={Number(registrationSettings?.currency?.id)}
              onChange={(e) => {
                const currency = currency_type.find(
                  (el) => Number(el.id) === Number(e)
                )
                updateRegistrationSettings({ currency: currency })
              }}
              options={currency_type.map((el) => ({
                text: el.name,
                value: Number(el.id),
              }))}
            />
            <div
              css={css`
                flex: 1;
              `}
            >
              <CurrencyInput
                placeholder="$0.00"
                type='text'
                onChange={({ target }) =>
                  updateRegistrationSettings({
                    standard_cost: target.value
                      ? parseFloat(target.value?.replace(/[^0-9.]/g, ''))
                      : '',
                  })
                }
                value={registrationSettings?.standard_cost}
                label='Cost per Registration'
                required
              />
            </div>
          </div>
          <SuspensionInput
            height={40}
            label='Official Governing Body Event Number (Hockey Canada/US)'
            id='event_number'
            type='number'
            min={0}
            step={1}
            value={registrationSettings?.official_governing_body_event_number}
            onChange={({ target }) =>
              updateRegistrationSettings({
                official_governing_body_event_number: target.value,
              })
            }
          />
          <RegistrationSettingsTitle
            css={css`
              margin-top: 32px;
            `}
          >
            Registration Period
          </RegistrationSettingsTitle>
          <div
            css={css`
              display: flex;
              gap: 20px;

              @media (max-width: 768px) {
                flex-direction: column;
                gap: 0;
                margin: 8px 0 12px 0;
              }
            `}
          >
            <div
              css={css`
                flex: 1;
                margin-bottom: 10px;
                min-width: 290px;
              `}
            >
              <DatePicker
                defaultValue={registrationSettings?.starts_at}
                datePlaceholder={dayjs().format('YYYY-MM-DD')}
                label='Registration Start'
                hideTime
                required
                name='starts_at'
                useISOString={false}
                changeOnReset
                onChange={({ target }) => {
                  const { value } = target

                  updateRegistrationSettings({ starts_at: value })
                }}
              />
            </div>
            <div
              css={css`
                flex: 1;
                min-width: 290px;
              `}
            >
              <DatePicker
                defaultValue={registrationSettings?.ends_at}
                datePlaceholder={dayjs().format('YYYY-MM-DD')}
                label='Registration End'
                hideTime
                required
                name='ends_at'
                useISOString={false}
                changeOnReset
                onChange={({ target }) => {
                  const { value } = target

                  updateRegistrationSettings({ ends_at: value })
                }}
              />
            </div>
          </div>
        </div>

        <div
          id='registration-body-right'
          css={css`
            flex: 1;
            padding-left: 20px;

            @media (max-width: 768px) {
              padding-left: 0;
              padding-top: 20px;
            }
          `}
        >
          <RegistrationSettingsTitle>
            Payments From SportNinja
          </RegistrationSettingsTitle>
          <div
            css={css`
              display: flex;
              gap: 20px;

              @media (max-width: 768px) {
                flex-direction: column;
                gap: 0;
                margin: 8px 0 12px 0;
              }
            `}
          >
            <SuspensionSelect
              showSeparator={false}
              useFullWidth
              enabledAllItemsOption={false}
              required
              label='Currency'
              placeholder='-'
              value={Number(registrationSettings?.payment_currency?.id)}
              onChange={(e) => {
                const currency = currency_type.find(
                  (el) => Number(el.id) === Number(e)
                )
                updateRegistrationSettings({ payment_currency: currency })
              }}
              options={currency_type.map((el) => ({
                text: el.name,
                value: Number(el.id),
              }))}
            />
            <SuspensionSelect
              useFullWidth
              showSeparator={false}
              enabledAllItemsOption={false}
              required
              label='Issue payment as'
              placeholder='-'
              value={Number(registrationSettings?.payment_type?.id)}
              onChange={(e) => {
                const currency = registration_payment_issue_type.find(
                  (el) => Number(el.id) === Number(e)
                )
                updateRegistrationSettings({ payment_type: currency })
              }}
              options={registration_payment_issue_type.map((el) => ({
                text: el.name,
                value: Number(el.id),
              }))}
            />
          </div>
          <RegistrationSettingsTitle
            css={css`
              margin-top: 25px;
            `}
          >
            Extra Options
          </RegistrationSettingsTitle>
          <Flex
            noFlex
            alignItems='center'
            justifyContent='space-between'
            css={css`
              padding: 8px 16px;
              height: 40px;
              border: 1px solid ${colors.SOFT_STEEL};
              border-radius: 4px;
              font-weight: 500;
              font-size: 16px;
              line-height: 16px;
              margin: 16px 0;
              transition: border-color 0.15s ease-in-out;

              i {
                transition: color 0.15s ease-in-out;
              }

              &:hover {
                border-color: white;
                cursor: pointer;

                i {
                  color: white;
                }
              }
            `}
            onClick={() => {
              navigator.clipboard.writeText(registrationSettings?.custom_link)
              setHasCopiedLink(true)
              setTimeout(() => {
                setHasCopiedLink(false)
              }, 2000)
            }}
          >
            <div
              css={css`
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                padding-right: 8px;
              `}
            >
              {hasCopiedLink ? 'Copied!' : registrationSettings?.custom_link}
            </div>

            <Icon faType='far' name='copy' color={colors.DEFAULT_FLAIR} />
          </Flex>
          <Flex noFlex>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                margin-right: 16px;
                flex: 1;

                input {
                  display: inline;
                  appearance: none;
                  opacity: 0;
                  width: 1px;
                }
              `}
            >
              <div
                css={css`
                  font-weight: 500;
                  font-size: 14px;
                  margin-bottom: 8px;
                `}
              >
                Custom background
              </div>
              <Flex>
                <label
                  htmlFor='background-image'
                  css={css`
                    display: flex;
                    flex: 1;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 16px;
                    height: 40px;
                    background: ${colors.HEADER};
                    border: 1px solid ${colors.SOFT_STEEL};
                    border-radius: 4px;
                    cursor: pointer;

                    transition: border-color 0.15s ease-in-out;

                    i {
                      transition: color 0.15s ease-in-out;
                    }

                    &:hover {
                      border-color: white;

                      i {
                        color: white;
                      }
                    }
                  `}
                >
                  {backgroundImage?.name || 'Select an image'}
                  <Icon
                    name='file-upload'
                    color={colors.DEFAULT_FLAIR}
                    fontSize={16}
                  />
                </label>

                <input
                  type='file'
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      setBackgroundImage(e.target.files[0])
                    }
                  }}
                  id='background-image'
                />
              </Flex>
            </div>
          </Flex>
          <div
            css={css`
              width: 100%;
              margin-top: 35px;
            `}
          >
            <ToggleSection
              title='Dark Theme'
              description='If enabled all texts will be presented in white.'
              isEnabled={registrationSettings?.dark_theme}
              onClick={() =>
                updateRegistrationSettings({
                  dark_theme: !registrationSettings?.dark_theme,
                })
              }
            />
          </div>
          <div
            css={css`
              margin-top: 28px;
            `}
          >
            <ToggleSection
              title='Go Live'
              description={
                registrationSettings?.is_live
                  ? 'Your registration will become available immediately.'
                  : registrationSettings?.starts_at &&
                    registrationSettings?.ends_at
                  ? `Your registration will become available for use during the period of ${dayjs(
                      registrationSettings?.starts_at
                    )?.format('MMMM DD, YYYY')}, to ${dayjs(
                      registrationSettings?.ends_at
                    )?.format('MMMM DD, YYYY')}`
                  : 'You can go live when you are ready to accept registrations.'
              }
              isEnabled={registrationSettings?.is_live}
              onClick={() =>
                updateRegistrationSettings({
                  is_live: !registrationSettings?.is_live,
                })
              }
            />
          </div>
        </div>
      </div>

      <Flex
        alignItems='center'
        justifyContent='center'
        css={css`
          margin-top: 32px;
        `}
      >
        <SuspensionSubmitButton onClick={onClose}>
          Cancel
        </SuspensionSubmitButton>
        <SuspensionSubmitButton
          onClick={async () => {
            if (backgroundImage) {
              await updateBackgroundImage()
            }

            onConfirm()
          }}
          isSolid
          css={css`
            margin-left: 40px;
          `}
        >
          Confirm
        </SuspensionSubmitButton>
      </Flex>
    </section>
  )
})

const EditRegistration = observer(
  ({ scheduleId, removeBorder, triggerReload }) => {
    const [isOpen, setIsOpen] = useState(false)
    const history = useHistory()
    const {
      registration: {
        getRegistrationSettings,
        updateRegistration,
        state: { enableRegistrationCog },
      },
    } = useMobxStore()

    useLayoutEffect(() => {
      if (isOpen) {
        getRegistrationSettings({
          scheduleId,
          level: 'schedule',
        })
      }
    }, [isOpen])

    const onEditRegistration = async (args) => {
      updateRegistration({
        level: 'schedule',
        scheduleId,
      })

      setIsOpen(false)

      if (triggerReload) {
        setTimeout(() => {
          history.go(0)
        }, 45)
      }
    }

    return (
      <div
        css={css`
          .search-container {
            z-index: 1000;
          }
        `}
      >
        {removeBorder ? (
          <button onClick={() => setIsOpen((o) => !o)}>
            <Icon
              name='cog'
              color={colors.DEFAULT_FLAIR}
              title='Edit Registration'
              fontSize={20}
            />
          </button>
        ) : (
          <>
            <BaseActionButton
              onClick={() => setIsOpen((o) => !o)}
              disabled={!enableRegistrationCog}
              name='cog'
              title='Edit Registration'
            />
          </>
        )}
        <SearchContainer displayContents={isOpen} isTeamOrSchedulePage>
          {isOpen && (
            <RegistrationSettings
              onConfirm={onEditRegistration}
              onClose={() => {
                setIsOpen(false)
              }}
            />
          )}
        </SearchContainer>
      </div>
    )
  }
)

export default EditRegistration
